/**
 * UpcomingOpportunitiesList - Unconverted leads with upcoming move dates
 * that can be shared with partner providers.
 */

import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Visibility,
  WhatsApp,
  OpenInNew,
  Transform,
} from "@mui/icons-material";
import { format } from "date-fns";
import ShareLeadWhatsAppModal from "../../leads/components/ShareLeadWhatsAppModal.jsx";
import { getLead } from "../../leads/services/leadService.js";
import { buildAdminJobBoardShareUrl } from "../../../shared/utils/pwaLinks.js";

const getBiddingChip = (biddingStatus) => {
  switch (biddingStatus) {
    case "open":
      return { label: "On job board", color: "success" };
    case "closed":
      return { label: "Bidding closed", color: "default" };
    default:
      return { label: "Not published", color: "warning" };
  }
};

const formatMoveDate = (opportunity) => {
  if (opportunity.moveTiming === "tbc") {
    return "Flexible / TBC";
  }
  if (opportunity.earliestMoveDate) {
    try {
      return format(new Date(opportunity.earliestMoveDate), "MMM dd, yyyy");
    } catch {
      return opportunity.moveDate || "TBC";
    }
  }
  return opportunity.moveDate || "TBC";
};

const UpcomingOpportunitiesList = ({ opportunities = [], loading = false }) => {
  const navigate = useNavigate();
  const [shareLead, setShareLead] = useState(null);
  const [shareLoadingId, setShareLoadingId] = useState(null);

  const handleShareWhatsApp = useCallback(async (opportunity) => {
    const leadId = opportunity.leadId || opportunity.id;
    if (!leadId) return;

    try {
      setShareLoadingId(leadId);
      const response = await getLead(leadId);
      if (response?.success && response.data) {
        setShareLead(response.data);
      }
    } finally {
      setShareLoadingId(null);
    }
  }, []);

  const handleOpenJobBoard = useCallback((opportunity) => {
    const url = buildAdminJobBoardShareUrl(opportunity.publicJobId);
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (opportunities.length === 0) {
    return (
      <Alert severity="info">
        No upcoming unconverted opportunities found. Leads with future move dates
        that have not been quoted yet will appear here for partner sharing.
      </Alert>
    );
  }

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        These are unconverted leads with upcoming move dates. Share them with
        partner providers via WhatsApp or publish to the job board from the lead
        detail page.
      </Alert>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Reference</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Route</TableCell>
              <TableCell>Move date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Job board</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {opportunities.map((opportunity) => {
              const leadId = opportunity.leadId || opportunity.id;
              const biddingChip = getBiddingChip(opportunity.biddingStatus);

              return (
                <TableRow key={leadId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {opportunity.referenceNumber || leadId?.slice(-8)}
                    </Typography>
                  </TableCell>
                  <TableCell>{opportunity.customerName || "—"}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                      {opportunity.from} → {opportunity.to}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatMoveDate(opportunity)}</TableCell>
                  <TableCell>
                    <Chip
                      label={opportunity.status || "new"}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={biddingChip.label}
                      size="small"
                      color={biddingChip.color}
                      variant={opportunity.biddingStatus === "open" ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={0.5} justifyContent="center">
                      <Tooltip title="View lead">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/leads/${leadId}`)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Create quote">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() =>
                            navigate(`/quotes/new?leadId=${leadId}`)
                          }
                        >
                          <Transform />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Share with provider (WhatsApp)">
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            disabled={shareLoadingId === leadId}
                            onClick={() => handleShareWhatsApp(opportunity)}
                          >
                            {shareLoadingId === leadId ? (
                              <CircularProgress size={18} />
                            ) : (
                              <WhatsApp />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>

                      {opportunity.publicJobId && (
                        <Tooltip title="Open job board listing">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenJobBoard(opportunity)}
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button variant="text" onClick={() => navigate("/leads")}>
          View all leads
        </Button>
      </Box>

      <ShareLeadWhatsAppModal
        open={Boolean(shareLead)}
        lead={shareLead}
        onClose={() => setShareLead(null)}
      />
    </>
  );
};

export default UpcomingOpportunitiesList;
